import { c as _c } from "react/compiler-runtime";
import { possibleTenantScopes, supportedTenantRoleKinds } from '@onefootprint/core';
import type { TenantRoleKindDiscriminant, TenantScope } from '@onefootprint/request-types/dashboard';
import { Form } from '@onefootprint/ui';
import type { ReactNode } from 'react';
import { useController, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useVaultProxyOptions } from '@/components/roles';
import type { Access } from '@/components/roles/utils/scope-environments';
import { canLimitToSandbox } from '@/components/roles/utils/scope-environments';
import useOrgSession from '@/hooks/use-org-session';
import { TenantFeatureGate } from '@/types/tenant';
import type { AccessOption } from '../access-select';
import AccessSelect from '../access-select';
import DecryptTable from '../decrypt-table';
import type { FormData } from '../role-form/role-form.types';
type PermissionsProps = {
  kind: TenantRoleKindDiscriminant;
};
type PermissionRowProps = {
  permission: TenantScope['kind'];
  kind: TenantRoleKindDiscriminant;
  label: string;
  hint: string;
  /** Every role has the permission. */
  isLocked?: boolean;
  /** Picks what the permission covers, such as the fields to decrypt. */
  config?: ReactNode;
  options?: AccessOption[];
  /** For a row whose access follows its `config`, in place of its own form value. */
  access?: Access;
  onAccessChange?: (access: Access) => void;
};
const PermissionRow = t0 => {
  const $ = _c(29);
  const {
    permission,
    kind,
    label,
    hint,
    isLocked,
    config,
    options: rowOptions,
    access: configAccess,
    onAccessChange
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "form.access"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("roles", t1);
  const t2 = `access.${permission}`;
  let t3;
  if ($[1] !== t2) {
    t3 = {
      name: t2
    };
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const {
    field
  } = useController(t3);
  const access = isLocked ? "sandbox_and_production" : configAccess ?? field.value ?? "none";
  let t4;
  if ($[3] !== kind || $[4] !== permission || $[5] !== t) {
    t4 = kind === "api_key" ? [{
      value: "none",
      label: t(_temp)
    }, {
      value: "sandbox_and_production",
      label: t(_temp2)
    }] : [{
      value: "none",
      label: t(_temp3)
    }, ...(canLimitToSandbox(permission) ? [{
      value: "sandbox_only" as const,
      label: t(_temp4)
    }] : []), {
      value: "sandbox_and_production",
      label: t(_temp5)
    }];
    $[3] = kind;
    $[4] = permission;
    $[5] = t;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const defaultOptions = t4;
  const options = rowOptions ?? defaultOptions;
  let t5;
  if ($[7] !== label) {
    t5 = <span className="text-body-2 text-primary">{label}</span>;
    $[7] = label;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== hint) {
    t6 = <span className="text-body-3 text-tertiary">{hint}</span>;
    $[9] = hint;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t5 || $[12] !== t6) {
    t7 = <div className="flex flex-col gap-0.5">{t5}{t6}</div>;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== label || $[15] !== t) {
    t8 = t(_temp6, {
      permission: label
    });
    $[14] = label;
    $[15] = t;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const t9 = onAccessChange ?? field.onChange;
  let t10;
  if ($[17] !== access || $[18] !== isLocked || $[19] !== options || $[20] !== t8 || $[21] !== t9) {
    t10 = <AccessSelect aria-label={t8} value={access} onChange={t9} options={options} disabled={isLocked} />;
    $[17] = access;
    $[18] = isLocked;
    $[19] = options;
    $[20] = t8;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t10 || $[24] !== t7) {
    t11 = <div className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_220px] sm:items-center sm:gap-6">{t7}{t10}</div>;
    $[23] = t10;
    $[24] = t7;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  const t12 = access !== "none" && config;
  let t13;
  if ($[26] !== t11 || $[27] !== t12) {
    t13 = <div className="flex flex-col gap-3 px-4 py-3 border-b border-tertiary last:border-b-0">{t11}{t12}</div>;
    $[26] = t11;
    $[27] = t12;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  return t13;
};
const Permissions = t0 => {
  const $ = _c(28);
  const {
    kind
  } = t0;
  const {
    t
  } = useTranslation("roles");
  const {
    options: proxyOptions
  } = useVaultProxyOptions();
  const featureGates = useOrgSession().data?.featureGates ?? [];
  const {
    control,
    setValue
  } = useFormContext();
  let t1;
  if ($[0] !== control) {
    t1 = {
      control,
      name: "decryptAccess"
    };
    $[0] = control;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const decryptAccess = useWatch(t1);
  let t2;
  if ($[2] !== control) {
    t2 = {
      control,
      name: "access.decrypt"
    };
    $[2] = control;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const decryptRowValue = useWatch(t2);
  let t3;
  if ($[4] !== decryptAccess || $[5] !== decryptRowValue) {
    t3 = Object.values(decryptAccess).some(_temp7) || (decryptRowValue ?? "none") !== "none";
    $[4] = decryptAccess;
    $[5] = decryptRowValue;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const isDecryptAllowed = t3;
  let t4;
  if ($[7] !== setValue) {
    t4 = access_0 => {
      setValue("access.decrypt", access_0);
      if (access_0 === "none") {
        setValue("decryptAccess", {});
      }
    };
    $[7] = setValue;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const handleDecryptRowChange = t4;
  let t5;
  if ($[9] !== proxyOptions) {
    t5 = proxyOptions.map(_temp8);
    $[9] = proxyOptions;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const flattenedProxyOptions = t5;
  let t6;
  if ($[11] !== featureGates || $[12] !== flattenedProxyOptions || $[13] !== handleDecryptRowChange || $[14] !== isDecryptAllowed || $[15] !== kind || $[16] !== t) {
    const supportedScopeKinds = possibleTenantScopes.filter(s => {
      if (s === "mcp" && kind === "api_key" && !featureGates.includes(TenantFeatureGate.McpApiKey)) {
        return false;
      }
      if (!kind) {
        return true;
      }
      return supportedTenantRoleKinds[s].includes(kind);
    });
    const rows = [{
      permission: "read",
      label: t(_temp9),
      hint: t(_temp10),
      isLocked: true
    }, {
      permission: "decrypt",
      label: t(_temp11),
      hint: t(_temp12),
      config: <DecryptTable kind={kind} />,
      options: [{
        value: "none",
        label: t(_temp13)
      }, {
        value: "sandbox_and_production",
        label: t(_temp14)
      }],
      access: isDecryptAllowed ? "sandbox_and_production" : "none",
      onAccessChange: handleDecryptRowChange
    }, {
      permission: "manual_review",
      label: t(_temp15),
      hint: t(_temp16)
    }, {
      permission: "write_entities",
      label: t(_temp17),
      hint: t(_temp18)
    }, {
      permission: "label_and_tag",
      label: t(_temp19),
      hint: t(_temp20)
    }, {
      permission: "api_keys",
      label: t(_temp21),
      hint: t(_temp22)
    }, {
      permission: "mcp",
      label: t(_temp23),
      hint: t(_temp24)
    }, {
      permission: "onboarding_configuration",
      label: t(_temp25),
      hint: t(_temp26)
    }, {
      permission: "manage_webhooks",
      label: t(_temp27),
      hint: t(_temp28)
    }, {
      permission: "manage_vault_proxy",
      label: t(_temp29),
      hint: t(_temp30)
    }, {
      permission: "org_settings",
      label: t(_temp31),
      hint: t(_temp32)
    }, {
      permission: "cip_integration",
      label: t(_temp33),
      hint: t(_temp34)
    }, {
      permission: "trigger_kyc",
      label: t(_temp35),
      hint: t(_temp36)
    }, {
      permission: "trigger_kyb",
      label: t(_temp37),
      hint: t(_temp38)
    }, {
      permission: "auth_token",
      label: t(_temp39),
      hint: t(_temp40)
    }, {
      permission: "onboarding",
      label: t(_temp41),
      hint: t(_temp42)
    }, {
      permission: "invoke_vault_proxy",
      label: t(_temp43),
      hint: t(_temp44),
      config: <Form.MultiSelect name="vaultProxyConfigs" size="compact" placeholder={t(_temp45)} options={flattenedProxyOptions} rules={{
        required: t(_temp46)
      }} />
    }, {
      permission: "bank_linking",
      label: t(_temp47),
      hint: t(_temp48)
    }];
    let t7;
    if ($[18] !== t) {
      t7 = t(_temp49);
      $[18] = t;
      $[19] = t7;
    } else {
      t7 = $[19];
    }
    let t8;
    if ($[20] !== t7) {
      t8 = <h2 className="text-label-2 text-primary">{t7}</h2>;
      $[20] = t7;
      $[21] = t8;
    } else {
      t8 = $[21];
    }
    let t9;
    if ($[22] !== kind || $[23] !== t) {
      t9 = kind === "api_key" && <p className="text-body-3 text-tertiary">{t(_temp50)}</p>;
      $[22] = kind;
      $[23] = t;
      $[24] = t9;
    } else {
      t9 = $[24];
    }
    let t10;
    if ($[25] !== t8 || $[26] !== t9) {
      t10 = <div className="flex flex-col gap-0.5">{t8}{t9}</div>;
      $[25] = t8;
      $[26] = t9;
      $[27] = t10;
    } else {
      t10 = $[27];
    }
    t6 = <div className="flex flex-col gap-3">{t10}{[{
        title: t(_temp51),
        rows: rows.filter(_temp52)
      }, {
        title: t(_temp53),
        rows: rows.filter(_temp54)
      }].map(section => {
        const supportedRows = section.rows.filter(row_1 => supportedScopeKinds.includes(row_1.permission));
        if (!supportedRows.length) {
          return null;
        }
        return <section key={section.title} className="flex flex-col gap-2"><h3 className="text-caption-2 text-tertiary">{section.title}</h3><div className="flex flex-col border border-tertiary rounded-default overflow-hidden">{supportedRows.map(row_2 => <PermissionRow key={row_2.permission} kind={kind} {...row_2} />)}</div></section>;
      })}</div>;
    $[11] = featureGates;
    $[12] = flattenedProxyOptions;
    $[13] = handleDecryptRowChange;
    $[14] = isDecryptAllowed;
    $[15] = kind;
    $[16] = t;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  return t6;
};
export type { PermissionsProps };
export default Permissions;
function _temp($) {
  return $.none;
}
function _temp2($_0) {
  return $_0.allow;
}
function _temp3($_1) {
  return $_1.none;
}
function _temp4($_2) {
  return $_2.sandboxOnly;
}
function _temp5($_3) {
  return $_3.sandboxAndProduction;
}
function _temp6($_4) {
  return $_4.label;
}
function _temp7(access) {
  return access && access !== "none";
}
function _temp8(option) {
  return {
    label: option.label,
    value: option.value
  };
}
function _temp9($) {
  return $.scopes.read;
}
function _temp10($_0) {
  return $_0.scopes.hints.read;
}
function _temp11($_1) {
  return $_1.form.decrypt.label;
}
function _temp12($_2) {
  return $_2.scopes.hints.decrypt;
}
function _temp13($_3) {
  return $_3.form.access.none;
}
function _temp14($_4) {
  return $_4.form.access.allow;
}
function _temp15($_5) {
  return $_5.scopes.manual_review;
}
function _temp16($_6) {
  return $_6.scopes.hints.manual_review;
}
function _temp17($_7) {
  return $_7.scopes.write_entities;
}
function _temp18($_8) {
  return $_8.scopes.hints.write_entities;
}
function _temp19($_9) {
  return $_9.scopes.label_and_tag;
}
function _temp20($_10) {
  return $_10.scopes.hints.label_and_tag;
}
function _temp21($_11) {
  return $_11.scopes.api_keys;
}
function _temp22($_12) {
  return $_12.scopes.hints.api_keys;
}
function _temp23($_13) {
  return $_13.scopes.mcp;
}
function _temp24($_14) {
  return $_14.scopes.hints.mcp;
}
function _temp25($_15) {
  return $_15.scopes.onboarding_configuration;
}
function _temp26($_16) {
  return $_16.scopes.hints.playbooks;
}
function _temp27($_17) {
  return $_17.scopes.manage_webhooks;
}
function _temp28($_18) {
  return $_18.scopes.hints.manage_webhooks;
}
function _temp29($_19) {
  return $_19.scopes.manage_vault_proxy;
}
function _temp30($_20) {
  return $_20.scopes.hints.manage_vault_proxy;
}
function _temp31($_21) {
  return $_21.scopes.org_settings;
}
function _temp32($_22) {
  return $_22.scopes.hints.org_settings;
}
function _temp33($_23) {
  return $_23.scopes.cip_integration;
}
function _temp34($_24) {
  return $_24.scopes.hints.cip_integration;
}
function _temp35($_25) {
  return $_25.scopes.trigger_kyc;
}
function _temp36($_26) {
  return $_26.scopes.hints.trigger_kyc;
}
function _temp37($_27) {
  return $_27.scopes.trigger_kyb;
}
function _temp38($_28) {
  return $_28.scopes.hints.trigger_kyb;
}
function _temp39($_29) {
  return $_29.scopes.auth_token;
}
function _temp40($_30) {
  return $_30.scopes.hints.auth_token;
}
function _temp41($_31) {
  return $_31.scopes.onboarding;
}
function _temp42($_32) {
  return $_32.scopes.hints.onboarding;
}
function _temp43($_33) {
  return $_33.scopes.invoke_vault_proxy.checkbox;
}
function _temp44($_34) {
  return $_34.scopes.hints.invoke_vault_proxy;
}
function _temp45($_35) {
  return $_35.form.proxyConfigs.label;
}
function _temp46($_36) {
  return $_36.form.proxyConfigs.errors.required;
}
function _temp47($_37) {
  return $_37.scopes.bank_linking;
}
function _temp48($_38) {
  return $_38.scopes.hints.bank_linking;
}
function _temp49($_39) {
  return $_39.form.permissions.title;
}
function _temp50($_40) {
  return $_40.form.permissions.apiKeyHint;
}
function _temp51($_41) {
  return $_41.form.permissions.general;
}
function _temp52(row) {
  return row.permission !== "decrypt";
}
function _temp53($_42) {
  return $_42.form.permissions.data;
}
function _temp54(row_0) {
  return row_0.permission === "decrypt";
}